import clsx from "clsx";
import { TextField, Autocomplete } from "@mui/material";
import useEquipmentTypes from "../../hooks/useEquipmentTypes";

// Function for dropdown option display - shows "abbr - description"
function getOptionLabel(option) {
  if (!option) return "";
  // If option is just a string (selected value), return it as is
  if (typeof option === "string") {
    return option;
  }
  // For dropdown options, show "abbr - description" with fallback
  const description = option.description || "No description";
  return `${option.abbr} - ${description}`;
}

function isSameValue(options, value) {
  return options?.abbr === value;
}

export default function EquipmentTypeLookup({
  className,
  formStateHandlers,
  fieldSetting,
  helperText,
  variant,
  required,
  inputProps,
  ...props
}) {
  const [equipmentTypes] = useEquipmentTypes();
  const { raw } = formStateHandlers;
  const fieldConfig = raw({
    ...fieldSetting,
    onChange: (input) => {
      const value = input.selectValue;
      fieldSetting.onChange({
        target: {
          value,
        },
      });
      return value;
    },
    validate: (value, _values, event) => {
      // Delibirately checking between event.selectValue and value
      // because when using tab to select an item,
      // 'value' might return undefined.
      return !required || !!event.selectValue || !!value;
    },
  });

  if (!equipmentTypes) {
    return null;
  }

  const fieldProps = {
    ...fieldConfig,
    ...props,
    onChange: (e, value) => {
      // Re-package value, useFormState does not pass through second arg
      // Handle null case when clear button is clicked
      e.selectValue = value ? value.abbr : null;
      return fieldConfig.onChange(e);
    },
    id: `lookup_${fieldConfig.name}`,
    className: clsx(className),
    getOptionSelected: isSameValue,
  };

  const finalInputProps = {
    label: props.label,
    error: props.error,
    variant,
    required,
    helperText,
    ...inputProps,
  };

  return (
    <Autocomplete
      {...fieldProps}
      options={equipmentTypes}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => isSameValue(option, value)}
      autoSelect={true}
      renderInput={(params) => (
        <TextField
          {...params}
          {...finalInputProps}
          fullWidth
          label={
            finalInputProps.required
              ? `${finalInputProps.label} (required)`
              : finalInputProps.label
          }
          error={props.data?.isFieldFailed}
          InputLabelProps={{
            // hard coded to true to position label above input
            shrink: true,
            // hard coded to false to prevent an asterisk from showing
            required: false,
          }}
        />
      )}
    />
  );
}
